import { create } from 'zustand';
import { ALL_MARKETS, MarketConfig } from '../config/markets';
import { api } from '../services/api';
import { useNotificationStore } from './notificationStore';


// Real oracle price fetcher for energy products
async function fetchOraclePrice(marketId: string): Promise<number | null> {
  try {
    const productMap: Record<string, string> = {
      'natgas-hh': 'NATGAS-HH', 'natgas-ttf': 'NATGAS-TTF',
      'crude-wti': 'CRUDE-WTI', 'crude-brent': 'CRUDE-BRENT',
      'gasoline-rbob': 'GASOLINE', 'ulsd': 'ULSD',
    };
    const product = productMap[marketId];
    if (product) {
      const r = await fetch('/api/energy/price?product=' + product);
      const d = await r.json();
      if (d.price) return d.price;
    }
    return null;
  } catch { return null; }
}

export interface Position {
  id: string;
  marketId: string;
  marketName: string;
  side: 'long' | 'short';
  size: number;
  entryPrice: number;
  markPrice: number;
  margin: number;
  leverage: number;
  liquidationPrice: number;
  stopLoss: number | null;
  takeProfit: number | null;
  unrealizedPnl: number;
  unrealizedPnlPct: number;
  openedAt: number;
}

export type OrderType = 'market' | 'limit' | 'stop' | 'stop-limit' | 'trailing-stop';
export type MarginMode = 'cross' | 'isolated';

export interface Order {
  id: string;
  marketId: string;
  marketName: string;
  side: 'long' | 'short';
  type: OrderType;
  size: number;
  price: number;
  stopPrice?: number;
  trailDistance?: number;
  trailPercent?: boolean;
  reduceOnly?: boolean;
  postOnly?: boolean;
  leverage: number;
  createdAt: number;
  status: 'open' | 'partially_filled';
  filled: number;
}

export interface Trade {
  id: string;
  marketId: string;
  marketName: string;
  side: 'long' | 'short';
  size: number;
  entryPrice: number;
  exitPrice: number;
  pnl: number;
  pnlPct: number;
  fee: number;
  closedAt: number;
}

export interface FundingPayment {
  id: string;
  marketId: string;
  marketName: string;
  rate: number;
  payment: number;
  side: 'long' | 'short';
  size: number;
  timestamp: number;
}

export interface MarketData {
  markPrice: number;
  indexPrice: number;
  change24h: number;
  change24hPct: number;
  volume24h: number;
  openInterest: number;
  fundingRate: number;
  nextFunding: number;
  nextSettlement: number;
  high24h: number;
  low24h: number;
}

export interface CandleData {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface WeatherData {
  temperature: number;
  humidity: number;
  windSpeed: number;
  windDirection: string;
  precipitation: number;
  conditions: string;
  lastUpdate: number;
  isStale: boolean;
  source: string;
  hddAccum: number;
  cddAccum: number;
  forecast: { day: string; high: number; low: number; conditions: string }[];
}

export interface TradeNotificationData {
  id: string;
  type: 'position_opened' | 'position_closed' | 'order_filled' | 'liquidation_warning';
  message: string;
  subMessage?: string;
  pnl?: number;
  timestamp: number;
}

interface PerpState {
  selectedMarket: MarketConfig | null;
  markets: MarketConfig[];
  marketData: Record<string, MarketData>;
  positions: Position[];
  orders: Order[];
  tradeHistory: Trade[];
  fundingHistory: FundingPayment[];
  weatherData: Record<string, WeatherData>;
  favorites: Set<string>;
  priceHistory: Record<string, CandleData[]>;
  marginMode: MarginMode;
  notifications: TradeNotificationData[];
  wsConnected: boolean;

  setSelectedMarket: (market: MarketConfig) => void;
  toggleFavorite: (marketId: string) => void;
  setMarginMode: (mode: MarginMode) => void;
  addNotification: (n: Omit<TradeNotificationData, 'id' | 'timestamp'>) => void;
  dismissNotification: (id: string) => void;
  openPosition: (params: {
    marketId: string;
    side: 'long' | 'short';
    size: number;
    leverage: number;
    price: number;
    stopLoss?: number;
    takeProfit?: number;
  }) => void;
  closePosition: (positionId: string, size?: number) => void;
  cancelOrder: (orderId: string) => void;
  updateMarkPrices: () => void;
  // WebSocket-driven methods
  applyWsPrices: (updates: WsPriceUpdate[]) => void;
  applyWsTrade: (trade: WsTradeUpdate) => void;
  setWsConnected: (connected: boolean) => void;
}

// Shape of a price update from the WebSocket
export interface WsPriceUpdate {
  marketId: string;
  markPrice: number;
  bid?: number;
  ask?: number;
  change24h?: number;
  volume24h?: number;
  openInterest?: number;
  fundingRate?: number;
  timestamp?: number;
}

// Shape of a trade update from the WebSocket
export interface WsTradeUpdate {
  marketId: string;
  price: number;
  size: number;
  side: 'long' | 'short';
  timestamp: number;
}

// Generate realistic mock data
function generateMockMarketData(): Record<string, MarketData> {
  const data: Record<string, MarketData> = {};
  const now = Date.now();

  ALL_MARKETS.forEach((m) => {
    let basePrice: number;
    // Oracle prices loaded asynchronously
    if (m.type === 'hdd') basePrice = 15 + Math.random() * 40;
    else if (m.type === "gas") basePrice = 2 + Math.random() * 50;
    else if (m.type === "oil") basePrice = 70 + Math.random() * 40;
    else if (m.type === "carbon") basePrice = 50 + Math.random() * 30;
    else if (m.type === "power") basePrice = 30 + Math.random() * 50;
    else if (m.type === "spread") basePrice = -5 + Math.random() * 20;
    else if (m.type === "freight") basePrice = 500 + Math.random() * 2000;
    else if (m.type === "product") basePrice = 2 + Math.random() * 5;
    else if (m.type === 'cdd') basePrice = 5 + Math.random() * 25;
    else basePrice = 20 + Math.random() * 50;

    const change = (Math.random() - 0.5) * basePrice * 0.08;
    data[m.id] = {
      markPrice: basePrice,
      indexPrice: basePrice + (Math.random() - 0.5) * 0.5,
      change24h: change,
      change24hPct: (change / basePrice) * 100,
      volume24h: Math.floor(5000 + Math.random() * 500000),
      openInterest: Math.floor(10000 + Math.random() * 2000000),
      fundingRate: (Math.random() - 0.5) * 0.002,
      nextFunding: now + 3600000 * (1 + Math.random() * 7),
      nextSettlement: now + 86400000 * (1 + Math.random() * 6),
      high24h: basePrice * (1 + Math.random() * 0.05),
      low24h: basePrice * (1 - Math.random() * 0.05),
    };
  });
  return data;
}

function generateMockPositions(marketData: Record<string, MarketData>): Position[] {
  const markets = ALL_MARKETS.filter(m => m.launchPhase === 1).slice(0, 8);
  return [
    // Mock positions removed for testnet
    // { id: 'pos-1', marketId: markets[0].id, marketName: markets[0].shortName, side: 'long' as const, size: 500, entryPrice: (marketData[markets[0].id]?.markPrice || 30) * 0.95, markPrice: marketData[markets[0].id]?.markPrice || 30, margin: 50, leverage: 10, liquidationPrice: (marketData[markets[0].id]?.markPrice || 30) * 0.85, stopLoss: (marketData[markets[0].id]?.markPrice || 30) * 0.9, takeProfit: (marketData[markets[0].id]?.markPrice || 30) * 1.15, unrealizedPnl: 0, unrealizedPnlPct: 0, openedAt: Date.now() - 3600000 * 4 },
    // { id: 'pos-2', marketId: markets[1].id, marketName: markets[1].shortName, side: 'short' as const, size: 300, entryPrice: (marketData[markets[1].id]?.markPrice || 25) * 1.03, markPrice: marketData[markets[1].id]?.markPrice || 25, margin: 30, leverage: 10, liquidationPrice: (marketData[markets[1].id]?.markPrice || 25) * 1.15, stopLoss: null, takeProfit: (marketData[markets[1].id]?.markPrice || 25) * 0.9, unrealizedPnl: 0, unrealizedPnlPct: 0, openedAt: Date.now() - 3600000 * 12 },
    // { id: 'pos-3', marketId: markets[2].id, marketName: markets[2].shortName, side: 'long' as const, size: 1000, entryPrice: (marketData[markets[2].id]?.markPrice || 45) * 0.98, markPrice: marketData[markets[2].id]?.markPrice || 45, margin: 100, leverage: 10, liquidationPrice: (marketData[markets[2].id]?.markPrice || 45) * 0.88, stopLoss: (marketData[markets[2].id]?.markPrice || 45) * 0.93, takeProfit: null, unrealizedPnl: 0, unrealizedPnlPct: 0, openedAt: Date.now() - 86400000 },
    // { id: 'pos-4', marketId: markets[3].id, marketName: markets[3].shortName, side: 'short' as const, size: 750, entryPrice: (marketData[markets[3].id]?.markPrice || 35) * 1.06, markPrice: marketData[markets[3].id]?.markPrice || 35, margin: 75, leverage: 10, liquidationPrice: (marketData[markets[3].id]?.markPrice || 35) * 1.18, stopLoss: (marketData[markets[3].id]?.markPrice || 35) * 1.1, takeProfit: (marketData[markets[3].id]?.markPrice || 35) * 0.92, unrealizedPnl: 0, unrealizedPnlPct: 0, openedAt: Date.now() - 86400000 * 2 },
    // { id: 'pos-5', marketId: markets[4].id, marketName: markets[4].shortName, side: 'long' as const, size: 200, entryPrice: (marketData[markets[4].id]?.markPrice || 28) * 0.92, markPrice: marketData[markets[4].id]?.markPrice || 28, margin: 20, leverage: 10, liquidationPrice: (marketData[markets[4].id]?.markPrice || 28) * 0.82, stopLoss: null, takeProfit: null, unrealizedPnl: 0, unrealizedPnlPct: 0, openedAt: Date.now() - 86400000 * 3 },
    // { id: 'pos-6', marketId: markets[5].id, marketName: markets[5].shortName, side: 'long' as const, size: 1500, entryPrice: (marketData[markets[5].id]?.markPrice || 22) * 0.97, markPrice: marketData[markets[5].id]?.markPrice || 22, margin: 150, leverage: 10, liquidationPrice: (marketData[markets[5].id]?.markPrice || 22) * 0.87, stopLoss: (marketData[markets[5].id]?.markPrice || 22) * 0.94, takeProfit: (marketData[markets[5].id]?.markPrice || 22) * 1.2, unrealizedPnl: 0, unrealizedPnlPct: 0, openedAt: Date.now() - 3600000 * 2 },
    // { id: 'pos-7', marketId: markets[6].id, marketName: markets[6].shortName, side: 'short' as const, size: 400, entryPrice: (marketData[markets[6].id]?.markPrice || 40) * 1.01, markPrice: marketData[markets[6].id]?.markPrice || 40, margin: 40, leverage: 10, liquidationPrice: (marketData[markets[6].id]?.markPrice || 40) * 1.13, stopLoss: null, takeProfit: (marketData[markets[6].id]?.markPrice || 40) * 0.95, unrealizedPnl: 0, unrealizedPnlPct: 0, openedAt: Date.now() - 3600000 * 8 },
  ].map(p => {
    const pnl = p.side === 'long'
      ? (p.markPrice - p.entryPrice) * p.size / p.entryPrice
      : (p.entryPrice - p.markPrice) * p.size / p.entryPrice;
    return { ...p, unrealizedPnl: pnl, unrealizedPnlPct: (pnl / p.margin) * 100 };
  });
}

function generateMockOrders(): Order[] {
  const markets = ALL_MARKETS.filter(m => m.launchPhase === 1).slice(2, 6);
  return markets.map((m, i) => ({
    id: `ord-${i + 1}`,
    marketId: m.id,
    marketName: m.shortName,
    side: i % 2 === 0 ? 'long' as const : 'short' as const,
    type: 'limit' as const,
    size: 100 + i * 200,
    price: 20 + i * 5 + (i % 2 === 0 ? -2 : 2),
    leverage: 5 + i * 2,
    createdAt: Date.now() - 3600000 * (i + 1),
    status: 'open' as const,
    filled: 0,
  }));
}

function generateMockTrades(): Trade[] {
  const markets = ALL_MARKETS.filter(m => m.launchPhase === 1).slice(0, 6);
  return markets.flatMap((m, i) => [
    {
      id: `trade-${i * 2 + 1}`,
      marketId: m.id,
      marketName: m.shortName,
      side: 'long' as const,
      size: 200 + i * 100,
      entryPrice: 25 + i * 3,
      exitPrice: 25 + i * 3 + (Math.random() - 0.3) * 5,
      pnl: 0, pnlPct: 0, fee: 0.5 + Math.random(),
      closedAt: Date.now() - 86400000 * (i + 1),
    },
    {
      id: `trade-${i * 2 + 2}`,
      marketId: m.id,
      marketName: m.shortName,
      side: 'short' as const,
      size: 150 + i * 80,
      entryPrice: 30 + i * 2,
      exitPrice: 30 + i * 2 - (Math.random() - 0.4) * 4,
      pnl: 0, pnlPct: 0, fee: 0.3 + Math.random(),
      closedAt: Date.now() - 86400000 * (i + 1) - 3600000 * 6,
    },
  ]).map(t => {
    const pnl = t.side === 'long'
      ? (t.exitPrice - t.entryPrice) * t.size / t.entryPrice - t.fee
      : (t.entryPrice - t.exitPrice) * t.size / t.entryPrice - t.fee;
    return { ...t, pnl, pnlPct: (pnl / (t.size / 10)) * 100 };
  });
}

function generateMockFunding(): FundingPayment[] {
  const markets = ALL_MARKETS.filter(m => m.launchPhase === 1).slice(0, 4);
  const payments: FundingPayment[] = [];
  markets.forEach((m, mi) => {
    for (let i = 0; i < 8; i++) {
      const rate = (Math.random() - 0.5) * 0.002;
      const side = mi % 2 === 0 ? 'long' as const : 'short' as const;
      const size = 200 + mi * 150;
      payments.push({
        id: `fund-${mi}-${i}`,
        marketId: m.id,
        marketName: m.shortName,
        rate,
        payment: -rate * size * (side === 'long' ? 1 : -1),
        side,
        size,
        timestamp: Date.now() - 3600000 * 8 * i,
      });
    }
  });
  return payments.sort((a, b) => b.timestamp - a.timestamp);
}

function generateCandleData(basePrice: number): CandleData[] {
  const candles: CandleData[] = [];
  let price = basePrice * (0.85 + Math.random() * 0.15);
  const now = Math.floor(Date.now() / 1000);
  for (let i = 200; i >= 0; i--) {
    const time = now - i * 3600;
    const change = (Math.random() - 0.48) * price * 0.03;
    const open = price;
    const close = price + change;
    const high = Math.max(open, close) + Math.random() * price * 0.015;
    const low = Math.min(open, close) - Math.random() * price * 0.015;
    candles.push({ time, open, high, low, close, volume: Math.floor(1000 + Math.random() * 50000) });
    price = close;
  }
  return candles;
}

function generateWeatherData(market: MarketConfig): WeatherData {
  const isUS = market.station.region === 'us' || market.station.region === 'canada';
  // Realistic March temperatures by city
  const cityTemps: Record<string, number> = {
    'Chicago': 45, 'New York': 48, 'Dallas': 65, 'Miami': 78, 'Houston': 70,
    'Atlanta': 58, 'Boston': 42, 'Los Angeles': 62, 'Seattle': 48, 'Washington DC': 52,
    'Detroit': 42, 'Minneapolis': 38, 'Denver': 45, 'Philadelphia': 48, 'Portland': 50,
    'Baltimore': 50, 'Cincinnati': 48, 'Des Moines': 42, 'Jacksonville': 68, 'Kansas City': 52,
    'Las Vegas': 65, 'Sacramento': 60, 'Salt Lake City': 48, 'Raleigh': 55, 'Colorado Springs': 45,
    'Toronto': 38, 'Montreal': 35, 'Vancouver': 46, 'Calgary': 35, 'Edmonton': 32, 'Winnipeg': 28,
    'London': 48, 'Paris': 50, 'Berlin': 42, 'Amsterdam': 45, 'Madrid': 55, 'Barcelona': 55,
    'Rome': 58, 'Stockholm': 35, 'Oslo': 32, 'Prague': 42, 'Essen': 44,
    'Tokyo': 55, 'Osaka': 52, 'Melbourne': 68, 'Sydney': 72, 'Brisbane': 75, 'Hiroshima': 52,
  };
  const baseTemp = cityTemps[market.station.city] || (isUS ? 50 + Math.random() * 15 : 8 + Math.random() * 10);
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const conditions = ['Sunny', 'Partly Cloudy', 'Cloudy', 'Rain', 'Snow', 'Clear', 'Overcast'];
  return {
    temperature: baseTemp,
    humidity: 40 + Math.random() * 40,
    windSpeed: 5 + Math.random() * 20,
    windDirection: ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'][Math.floor(Math.random() * 8)],
    precipitation: Math.random() > 0.6 ? Math.random() * 0.5 : 0,
    conditions: conditions[Math.floor(Math.random() * conditions.length)],
    lastUpdate: Date.now() - Math.floor(Math.random() * 600000),
    isStale: Math.random() > 0.85,
    source: isUS ? 'NOAA GHCN-d' : 'ECMWF',
    hddAccum: isUS ? Math.max(0, (65 - baseTemp) * 7) : Math.max(0, (18 - baseTemp) * 7),
    cddAccum: isUS ? Math.max(0, (baseTemp - 65) * 7) : Math.max(0, (baseTemp - 18) * 7),
    forecast: days.map(d => ({
      day: d,
      high: baseTemp + 3 + Math.random() * 5,
      low: baseTemp - 3 - Math.random() * 5,
      conditions: conditions[Math.floor(Math.random() * conditions.length)],
    })),
  };
}

const initialMarketData = generateMockMarketData();

export const usePerpStore = create<PerpState>((set, get) => {
  const defaultMarket = ALL_MARKETS.find(m => m.id === 'hdd-new-york-7d') || ALL_MARKETS[0];

  // Generate initial price history for all phase 1 markets
  const priceHistory: Record<string, CandleData[]> = {};
  ALL_MARKETS.filter(m => m.launchPhase === 1).forEach(m => {
    priceHistory[m.id] = generateCandleData(initialMarketData[m.id]?.markPrice || 30);
  });

  // Generate weather data for phase 1
  const weatherData: Record<string, WeatherData> = {};
  ALL_MARKETS.filter(m => m.launchPhase === 1).forEach(m => {
    weatherData[m.id] = generateWeatherData(m);
  });

  return {
    selectedMarket: defaultMarket,
    markets: ALL_MARKETS,
    marketData: initialMarketData,
    positions: generateMockPositions(initialMarketData),
    orders: generateMockOrders(),
    tradeHistory: generateMockTrades(),
    fundingHistory: generateMockFunding(),
    weatherData,
    favorites: new Set(['hdd-new-york-7d', 'cdd-new-york-7d', 'hdd-chicago-7d', 'temp-los-angeles-7d', 'hdd-london-7d']),
    priceHistory,
    marginMode: (localStorage.getItem('marginMode') as MarginMode) || 'isolated',
    notifications: [],
    wsConnected: false,

    setMarginMode: (mode) => {
      localStorage.setItem('marginMode', mode);
      set({ marginMode: mode });
    },

    addNotification: (n) => set(state => {
      const notif: TradeNotificationData = {
        ...n,
        id: `notif-${Date.now()}-${Math.random()}`,
        timestamp: Date.now(),
      };
      return { notifications: [notif, ...state.notifications].slice(0, 5) };
    }),

    dismissNotification: (id) => set(state => ({
      notifications: state.notifications.filter(n => n.id !== id),
    })),

    setSelectedMarket: (market) => {
      set({ selectedMarket: market });
      // Generate price history on demand
      const state = get();
      if (!state.priceHistory[market.id]) {
        const md = state.marketData[market.id];
        const ph = { ...state.priceHistory };
        ph[market.id] = generateCandleData(md?.markPrice || 30);
        set({ priceHistory: ph });
      }
      if (!state.weatherData[market.id]) {
        const wd = { ...state.weatherData };
        wd[market.id] = generateWeatherData(market);
        set({ weatherData: wd });
      }
    },

    toggleFavorite: (marketId) => set(state => {
      const f = new Set(state.favorites);
      f.has(marketId) ? f.delete(marketId) : f.add(marketId);
      return { favorites: f };
    }),

    openPosition: (params) => set(state => {
      const market = ALL_MARKETS.find(m => m.id === params.marketId);
      if (!market) return state;
      const margin = params.size / params.leverage;
      const liqDist = margin / params.size;
      const liqPrice = params.side === 'long'
        ? params.price * (1 - liqDist * 0.9)
        : params.price * (1 + liqDist * 0.9);
      const pos: Position = {
        id: `pos-${Date.now()}`,
        marketId: params.marketId,
        marketName: market.shortName,
        side: params.side,
        size: params.size,
        entryPrice: params.price,
        markPrice: params.price,
        margin,
        leverage: params.leverage,
        liquidationPrice: liqPrice,
        stopLoss: params.stopLoss || null,
        takeProfit: params.takeProfit || null,
        unrealizedPnl: 0,
        unrealizedPnlPct: 0,
        openedAt: Date.now(),
      };
      const notif: TradeNotificationData = {
        id: `notif-${Date.now()}`,
        type: 'position_opened',
        message: `Position Opened: ${params.side === 'long' ? 'Long' : 'Short'} ${market.shortName} @ ${params.price.toFixed(2)} (${params.leverage}x)`,
        timestamp: Date.now(),
      };
      // Record trade for points/XP (fire-and-forget)
      const walletAddress = (window as any).__tonWalletAddress;
      if (walletAddress) {
        api.recordPointsTrade({
          address: walletAddress,
          market_id: params.marketId,
          side: params.side === 'long' ? 'LONG' : 'SHORT',
          size: params.size,
          entry_price: params.price,
          pnl: 0,
        }).catch(() => {});
      }
      // Push to notification center
      useNotificationStore.getState().addNotification({
        type: 'trade_executed',
        title: 'Trade Executed',
        description: `${params.side === 'long' ? 'Long' : 'Short'} ${market.shortName} @ ${params.price.toFixed(2)} · ${params.leverage}× leverage`,
      });
      return { positions: [...state.positions, pos], notifications: [notif, ...state.notifications].slice(0, 5) };
    }),

    closePosition: (positionId, size) => set(state => {
      const pos = state.positions.find(p => p.id === positionId);
      if (!pos) return state;
      const trade: Trade = {
        id: `trade-${Date.now()}`,
        marketId: pos.marketId,
        marketName: pos.marketName,
        side: pos.side,
        size: size || pos.size,
        entryPrice: pos.entryPrice,
        exitPrice: pos.markPrice,
        pnl: pos.unrealizedPnl,
        pnlPct: pos.unrealizedPnlPct,
        fee: (size || pos.size) * 0.001,
        closedAt: Date.now(),
      };
      const pnlStr = pos.unrealizedPnl >= 0 ? `+$${pos.unrealizedPnl.toFixed(2)}` : `-$${Math.abs(pos.unrealizedPnl).toFixed(2)}`;
      const notif: TradeNotificationData = {
        id: `notif-${Date.now()}`,
        type: 'position_closed',
        message: `Position Closed: ${pnlStr} P&L`,
        subMessage: `${pos.side === 'long' ? 'Long' : 'Short'} ${pos.marketName}`,
        pnl: pos.unrealizedPnl,
        timestamp: Date.now(),
      };
      // Record closed trade for points/XP (fire-and-forget)
      const walletAddressClose = (window as any).__tonWalletAddress;
      if (walletAddressClose) {
        api.recordPointsTrade({
          address: walletAddressClose,
          market_id: pos.marketId,
          side: pos.side === 'long' ? 'LONG' : 'SHORT',
          size: size || pos.size,
          entry_price: pos.entryPrice,
          exit_price: pos.markPrice,
          pnl: pos.unrealizedPnl,
        }).catch(() => {});
      }
      // Push to notification center
      const closedPnlStr = pos.unrealizedPnl >= 0
        ? `+$${pos.unrealizedPnl.toFixed(2)}`
        : `-$${Math.abs(pos.unrealizedPnl).toFixed(2)}`;
      useNotificationStore.getState().addNotification({
        type: 'trade_executed',
        title: 'Position Closed',
        description: `${pos.side === 'long' ? 'Long' : 'Short'} ${pos.marketName} · P&L ${closedPnlStr}`,
        meta: { pnl: pos.unrealizedPnl },
      });
      if (size && size < pos.size) {
        const ratio = size / pos.size;
        return {
          positions: state.positions.map(p => p.id === positionId
            ? { ...p, size: p.size - size, margin: p.margin * (1 - ratio), unrealizedPnl: p.unrealizedPnl * (1 - ratio) }
            : p),
          tradeHistory: [trade, ...state.tradeHistory],
          notifications: [notif, ...state.notifications].slice(0, 5),
        };
      }
      return {
        positions: state.positions.filter(p => p.id !== positionId),
        tradeHistory: [trade, ...state.tradeHistory],
        notifications: [notif, ...state.notifications].slice(0, 5),
      };
    }),

    cancelOrder: (orderId) => set(state => ({
      orders: state.orders.filter(o => o.id !== orderId),
    })),

    setWsConnected: (connected) => set({ wsConnected: connected }),
    
    // Update a single market's price from oracle
    updateMarketPrice: (marketId: string, price: number) => {
      set((state) => {
        const md = { ...state.marketData };
        if (md[marketId]) {
          md[marketId] = {
            ...md[marketId],
            markPrice: price,
            indexPrice: price,
          };
        }
        return { marketData: md };
      });
    },

    // Apply a batch of live price updates from the WebSocket
    applyWsPrices: (updates: WsPriceUpdate[]) => set(state => {
      const md = { ...state.marketData };
      let changed = false;

      for (const u of updates) {
        const existing = md[u.marketId];
        if (!existing) continue;
        changed = true;
        md[u.marketId] = {
          ...existing,
          markPrice: u.markPrice,
          indexPrice: u.markPrice, // treat as index too
          change24h: u.change24h !== undefined ? u.change24h : existing.change24h,
          change24hPct: u.change24h !== undefined ? (u.change24h / u.markPrice) * 100 : existing.change24hPct,
          volume24h: u.volume24h !== undefined ? u.volume24h : existing.volume24h,
          openInterest: u.openInterest !== undefined ? u.openInterest : existing.openInterest,
          fundingRate: u.fundingRate !== undefined ? u.fundingRate : existing.fundingRate,
          high24h: Math.max(existing.high24h, u.markPrice),
          low24h: Math.min(existing.low24h, u.markPrice),
        };
      }

      if (!changed) return state;

      // Update positions PnL
      const positions = state.positions.map(p => {
        const mark = md[p.marketId]?.markPrice || p.markPrice;
        const pnl = p.side === 'long'
          ? (mark - p.entryPrice) * p.size / p.entryPrice
          : (p.entryPrice - mark) * p.size / p.entryPrice;
        return { ...p, markPrice: mark, unrealizedPnl: pnl, unrealizedPnlPct: (pnl / p.margin) * 100 };
      });

      // Update price chart for selected market
      const sel = state.selectedMarket;
      const ph = { ...state.priceHistory };
      if (sel && ph[sel.id] && md[sel.id]) {
        const candles = [...ph[sel.id]];
        const last = candles[candles.length - 1];
        const now = Math.floor(Date.now() / 1000);
        const mp = md[sel.id].markPrice;
        if (now - last.time >= 3600) {
          candles.push({ time: now, open: last.close, high: mp, low: mp, close: mp, volume: Math.floor(Math.random() * 2000) });
        } else {
          const c = { ...last };
          c.close = mp;
          c.high = Math.max(c.high, mp);
          c.low = Math.min(c.low, mp);
          c.volume += Math.floor(Math.random() * 100);
          candles[candles.length - 1] = c;
        }
        ph[sel.id] = candles;
      }

      return { marketData: md, positions, priceHistory: ph };
    }),

    // Apply a single trade event from the WebSocket
    applyWsTrade: (_trade: WsTradeUpdate) => {
      // Currently trades from WS are displayed via the recent trades feed in components.
      // The store doesn't need to persist all WS trades — just no-op here.
      // Components can subscribe to the WS hook directly for trade ticks if needed.
    },

    updateMarkPrices: () => set(state => {
      const md = { ...state.marketData };
      Object.keys(md).forEach(id => {
        const d = md[id];
        const tick = (Math.random() - 0.48) * d.markPrice * 0.003;
        md[id] = {
          ...d,
          markPrice: d.markPrice + tick,
          change24h: d.change24h + tick,
          change24hPct: ((d.change24h + tick) / (d.markPrice + tick)) * 100,
        };
      });
      const positions = state.positions.map(p => {
        const mark = md[p.marketId]?.markPrice || p.markPrice;
        const pnl = p.side === 'long'
          ? (mark - p.entryPrice) * p.size / p.entryPrice
          : (p.entryPrice - mark) * p.size / p.entryPrice;
        return { ...p, markPrice: mark, unrealizedPnl: pnl, unrealizedPnlPct: (pnl / p.margin) * 100 };
      });

      // Append new candle tick to selected market
      const sel = state.selectedMarket;
      const ph = { ...state.priceHistory };
      if (sel && ph[sel.id]) {
        const candles = [...ph[sel.id]];
        const last = candles[candles.length - 1];
        const now = Math.floor(Date.now() / 1000);
        const mp = md[sel.id]?.markPrice || last.close;
        if (now - last.time >= 3600) {
          candles.push({ time: now, open: last.close, high: mp, low: mp, close: mp, volume: Math.floor(Math.random() * 10000) });
        } else {
          const c = { ...last };
          c.close = mp;
          c.high = Math.max(c.high, mp);
          c.low = Math.min(c.low, mp);
          c.volume += Math.floor(Math.random() * 500);
          candles[candles.length - 1] = c;
        }
        ph[sel.id] = candles;
      }

      return { marketData: md, positions, priceHistory: ph };
    }),
  };
});
